import React, { useEffect, useState } from 'react'
import Container from '@embracesbs/component-container/Container'
import Button2 from '@embracesbs/component-button2/Button2'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import TextField from '@embracesbs/component-textfield/TextField'
import Accordion from '@embracesbs/component-accordion/Accordion'
import Modal from '@embracesbs/component-modal'
import Icon from '@embracesbs/component-icon'
import Text from '@embracesbs/component-text'
import { TextHeading } from '../../StyledComponents/Components'
import { Notification, PRIORITY_INFO } from '../../../Interfaces/Types'
import { useTenants } from '../../Context/TenantContext'
import { useApi, useAuth } from '../../../Hooks'
import NotificationItem from './NotificationItem'
import { SEVERITIES, editableSeverity, isKnownSeverity, sevMeta } from '../../../Utilities/notificationSeverity'

const FONT = "'F-Default', Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

const listStyle: React.CSSProperties = { maxHeight: '90%', overflow: 'auto', paddingRight: '15px', fontFamily: FONT }
const cardStyle: React.CSSProperties = {
  border: '1px solid var(--tma-list-card-border)',
  borderRadius: '12px',
  padding: '20px',
  background: 'var(--tma-panel-bg)',
  boxShadow: '0 1px 3px rgba(16,18,24,0.05)'
}

const pad = (n: number) => String(n).padStart(2, '0')
// datetime-local value <-> UTC ISO round-trip (admin works in local time).
const localToUtcIso = (local: string): string => (local ? new Date(local).toISOString() : '')
const utcToLocalInput = (iso?: string): string => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const shortDate = (iso?: string): string => {
  if (!iso) return '-'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '-' : d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}
const rowTitle = (n: Notification): string => {
  const c = (n?.content ?? {}) as unknown as Record<string, { title?: string }>
  return c.nl?.title || c.en?.title || Object.values(c)[0]?.title || '(no title)'
}

// The API rejects a body it cannot bind with a reason worth reading; a bare
// 'Failed to save' turns a one-minute fix into a guessing game.
const saveErrorMessage = (err: any): string => {
  const data = err?.data
  if (typeof data === 'string' && data.length > 0) return data
  const detail = data?.detail || data?.title
  if (typeof detail === 'string' && detail.length > 0) return detail
  return err?.message ?? 'Failed to save notifications.'
}

const emptyForm = { severity: PRIORITY_INFO, start: '', end: '', titleNl: '', mainNl: '', titleEn: '', mainEn: '' }
type FormErrors = { start?: boolean; end?: boolean; titleNl?: boolean }

const NotificationsList: React.FC = () => {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const { selectedTenantId } = useTenants()

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [form, setForm] = useState({ ...emptyForm })
  // Languages beyond nl/en entered via the JSON editor — preserved on save.
  const [extraLangs, setExtraLangs] = useState<Record<string, { title: string; main: string }>>({})
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [errors, setErrors] = useState<FormErrors>({})
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [jsonDraft, setJsonDraft] = useState<string>('')
  const [jsonError, setJsonError] = useState<string>('')

  useEffect(() => {
    if (selectedTenantId !== null && isLoggedIn) fetchNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, isLoggedIn])

  const fetchNotifications = async () => {
    try {
      const response = await fetchApi({
        endpoint: `/api/tenant/${selectedTenantId}/tenantsconfigurationruntime/notifications`,
        method: 'GET'
      })
      setNotifications(Array.isArray(response) ? response : [])
    } catch (err) {
      console.error(err)
    }
  }

  const setField = (name: keyof typeof emptyForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [name]: value }))
    if (name === 'start' || name === 'end' || name === 'titleNl') {
      setErrors((prev) => ({ ...prev, [name]: false }))
    }
  }

  const resetForm = () => {
    setForm({ ...emptyForm })
    setExtraLangs({})
    setEditIndex(null)
    setErrors({})
    setErrorMsg('')
    setJsonDraft('')
    setJsonError('')
  }

  const accent = sevMeta(form.severity).color

  const handleSubmit = () => {
    const endBeforeStart = !!form.start && !!form.end && new Date(form.end).getTime() <= new Date(form.start).getTime()
    const nextErrors: FormErrors = { start: !form.start, end: !form.end || endBeforeStart, titleNl: !form.titleNl.trim() }

    if (nextErrors.start || nextErrors.end || nextErrors.titleNl) {
      setErrors(nextErrors)
      setErrorMsg(endBeforeStart ? 'End must be after start.' : 'Fill in the required fields to save the notification.')
      return
    }

    // NL is primary; EN falls back to the Dutch text when left empty.
    const nlTitle = form.titleNl.trim()
    const nlMain = form.mainNl.trim()
    const notification = {
      severity: form.severity,
      starttime: localToUtcIso(form.start),
      endtime: localToUtcIso(form.end),
      content: {
        ...extraLangs,
        nl: { title: nlTitle, main: nlMain },
        en: { title: form.titleEn.trim() || nlTitle, main: form.mainEn.trim() || nlMain }
      }
    } as Notification

    const updated =
      editIndex !== null ? notifications.map((n, i) => (i === editIndex ? notification : n)) : [...notifications, notification]
    updateNotifications(updated)
  }

  const updateNotifications = async (updatedArray: Notification[]) => {
    try {
      const response = await fetchApi({
        endpoint: `/api/tenant/${selectedTenantId}/tenantsconfigurationruntime/notifications`,
        method: 'POST',
        body: updatedArray
      })
      setNotifications(Array.isArray(response) ? response : updatedArray)
      resetForm()
    } catch (err) {
      console.error(err)
      setErrorMsg(saveErrorMessage(err))
    }
  }

  const startEdit = (index: number) => {
    const n = notifications[index]
    const c = (n?.content ?? {}) as unknown as Record<string, { title?: string; main?: string }>
    const rest: Record<string, { title: string; main: string }> = {}
    Object.keys(c)
      .filter((lang) => lang !== 'nl' && lang !== 'en')
      .forEach((lang) => {
        rest[lang] = { title: c[lang]?.title ?? '', main: c[lang]?.main ?? '' }
      })
    setExtraLangs(rest)
    setForm({
      severity: editableSeverity(n.severity),
      start: utcToLocalInput(n.starttime),
      end: utcToLocalInput(n.endtime),
      titleNl: c.nl?.title ?? '',
      mainNl: c.nl?.main ?? '',
      titleEn: c.en?.title ?? '',
      mainEn: c.en?.main ?? ''
    })
    setEditIndex(index)
    setErrors({})
    setErrorMsg('')
    setOpenIndex(null)
  }

  const deleteNotification = (index: number) => {
    setOpenIndex(null)
    updateNotifications(notifications.filter((_, i) => i !== index))
  }

  const loadJsonFromFields = () => {
    setJsonError('')
    setJsonDraft(
      JSON.stringify(
        { nl: { title: form.titleNl, main: form.mainNl }, en: { title: form.titleEn, main: form.mainEn }, ...extraLangs },
        null,
        2
      )
    )
  }

  const applyJsonToFields = () => {
    try {
      const parsed = JSON.parse(jsonDraft) ?? {}
      const rest: Record<string, { title: string; main: string }> = {}
      Object.keys(parsed)
        .filter((lang) => lang !== 'nl' && lang !== 'en')
        .forEach((lang) => {
          rest[lang] = { title: parsed[lang]?.title ?? '', main: parsed[lang]?.main ?? '' }
        })
      setExtraLangs(rest)
      setForm((prev) => ({
        ...prev,
        titleNl: parsed?.nl?.title ?? '',
        mainNl: parsed?.nl?.main ?? '',
        titleEn: parsed?.en?.title ?? '',
        mainEn: parsed?.en?.main ?? ''
      }))
      setJsonError('')
    } catch {
      setJsonError('Invalid JSON - expected { "nl": { "title", "main" }, "en": { … } }')
    }
  }

  const isEditing = editIndex !== null

  return (
    <div style={listStyle}>
      <TextHeading style={{ marginBottom: '10px' }}>{isEditing ? 'Edit notification' : 'Add notification'}</TextHeading>

      {/* Input card — the left accent stripe previews the chosen severity. */}
      <div style={{ ...cardStyle, borderLeft: `4px solid ${accent}` }}>
        <Container>
          <TextFieldWrapper labelText='Start * (your local time)'>
            <div className='notif-datetime'>
              <TextField
                type='datetime-local'
                value={form.start}
                onChange={(e) => setField('start', e.target.value)}
                variant='default'
                hasAnError={errors.start}
              />
            </div>
          </TextFieldWrapper>
          <TextFieldWrapper labelText='End * (your local time)'>
            <div className='notif-datetime'>
              <TextField
                type='datetime-local'
                value={form.end}
                onChange={(e) => setField('end', e.target.value)}
                variant='default'
                hasAnError={errors.end}
              />
            </div>
          </TextFieldWrapper>
        </Container>

        {/* Severity as a clickable, colored pill set. */}
        <div style={{ margin: '4px 0 14px' }}>
          <label
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: 'var(--tma-text-muted)'
            }}
          >
            Severity
          </label>
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
            {SEVERITIES.map((s) => {
              const active = form.severity === s.value
              return (
                <button
                  key={s.value}
                  type='button'
                  onClick={() => setField('severity', s.value)}
                  style={{
                    fontFamily: FONT,
                    cursor: 'pointer',
                    borderRadius: '999px',
                    padding: '6px 16px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    border: `1px solid ${s.color}`,
                    color: active ? '#ffffff' : s.color,
                    background: active ? s.color : 'transparent',
                    transition: 'background 0.15s, color 0.15s'
                  }}
                >
                  {s.label}
                </button>
              )
            })}
          </div>
          {!isKnownSeverity(form.severity) && (
            <Text textStyle='description' textColor='grey' style={{ display: 'block', marginTop: '6px' }}>
              Stored severity {String(form.severity)} is not one of the three above. It is kept as-is on save - pick a severity only if you
              mean to change it.
            </Text>
          )}
        </div>

        <Container>
          <TextFieldWrapper labelText='Title (NL) *'>
            <TextField
              value={form.titleNl}
              onChange={(e) => setField('titleNl', e.target.value)}
              type='text'
              variant='default'
              hasAnError={errors.titleNl}
            />
          </TextFieldWrapper>
          <TextFieldWrapper labelText='Title (EN) - optional, falls back to Dutch'>
            <TextField value={form.titleEn} onChange={(e) => setField('titleEn', e.target.value)} type='text' variant='default' />
          </TextFieldWrapper>
        </Container>
        <Container>
          <TextFieldWrapper labelText='Content (NL)'>
            <TextField isTextArea value={form.mainNl} onChange={(e) => setField('mainNl', e.target.value)} type='text' variant='default' />
          </TextFieldWrapper>
          <TextFieldWrapper labelText='Content (EN) - optional, falls back to Dutch'>
            <TextField isTextArea value={form.mainEn} onChange={(e) => setField('mainEn', e.target.value)} type='text' variant='default' />
          </TextFieldWrapper>
        </Container>
        {Object.keys(extraLangs).length > 0 && (
          <Text textStyle='description' textColor='grey' style={{ display: 'block', marginTop: '4px' }}>
            + extra languages kept from JSON: {Object.keys(extraLangs).join(', ')}
          </Text>
        )}

        <Accordion id='notification-json' headerTitle='Advanced: edit content as JSON' isFullWidth={true}>
          <Container isVertical align='left'>
            <Text textStyle='description' textColor='grey'>
              Load the fields above into JSON, edit, then apply back. On save the fields are used. Extra languages (e.g. "de") are kept.
            </Text>
            <TextField
              value={jsonDraft}
              onChange={(e) => setJsonDraft(e.target.value)}
              isTextArea
              type='text'
              variant='default'
              placeholder='{ "nl": { "title": "", "main": "" }, "en": { "title": "", "main": "" } }'
              {...(jsonError ? { hasAnError: true, errorMessage: jsonError } : {})}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <Button2 buttonRadius='8px' buttonSize='30px' text='Load from fields' variant='outlined' onClick={loadJsonFromFields} />
              <Button2 buttonRadius='8px' buttonSize='30px' text='Apply to fields' variant='outlined' onClick={applyJsonToFields} />
            </div>
          </Container>
        </Accordion>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
          {errorMsg && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'var(--tma-danger-text)',
                fontSize: '13px',
                fontWeight: 600
              }}
            >
              <Icon iconName='AlertTriangle' size={15} color='var(--tma-danger-text)' />
              {errorMsg}
            </span>
          )}
          <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
            {isEditing && <Button2 buttonRadius='8px' buttonSize='40px' text='Cancel edit' variant='outlined' onClick={resetForm} />}
            <Button2
              buttonRadius='8px'
              buttonSize='40px'
              text={isEditing ? 'Save changes' : 'Add notification'}
              onClick={handleSubmit}
              variant='contained'
            />
          </div>
        </div>
      </div>

      <TextHeading style={{ margin: '20px 0 10px' }}>Notifications</TextHeading>
      {notifications.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {notifications.map((notification, index) => {
            const sev = sevMeta(notification.severity)
            return (
              <div
                key={index}
                role='button'
                tabIndex={0}
                onClick={() => setOpenIndex(index)}
                onKeyDown={(e) => e.key === 'Enter' && setOpenIndex(index)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '12px 16px',
                  border: '1px solid var(--tma-list-card-border)',
                  borderLeft: `4px solid ${sev.color}`,
                  borderRadius: '10px',
                  background: 'var(--tma-panel-bg)',
                  cursor: 'pointer'
                }}
              >
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    color: sev.color,
                    background: sev.bg,
                    padding: '3px 9px',
                    borderRadius: '999px',
                    minWidth: '54px',
                    textAlign: 'center'
                  }}
                >
                  {sev.label}
                </span>
                <span
                  style={{ flex: 1, fontWeight: 600, fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {rowTitle(notification)}
                </span>
                <span style={{ color: 'var(--tma-text-muted)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                  {shortDate(notification.starttime)} – {shortDate(notification.endtime)}
                </span>
                <Icon iconName='ArrowRight1' size={14} color='var(--tma-text-faint)' />
              </div>
            )
          })}
        </div>
      ) : (
        <Text
          textStyle='description'
          textColor='grey'
          content='No notifications available.'
          style={{ display: 'block', marginLeft: '4px' }}
        />
      )}

      {/* Detail dialog — closes on the X, the Close button, an outside click, or Esc. */}
      <Modal
        isOpen={openIndex !== null}
        width='45%'
        height='auto'
        onClose={() => setOpenIndex(null)}
        closeOnOverlayClick
        closeOnEsc
        showCloseIcon
        hasHeaderBorder={true}
        headerTitle='Notification details'
      >
        {openIndex !== null && notifications[openIndex] && (
          <div style={{ fontFamily: FONT }}>
            <NotificationItem notification={notifications[openIndex]} />
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                marginTop: '18px',
                paddingTop: '14px',
                borderTop: '1px solid var(--tma-card-border)'
              }}
            >
              <Button2
                buttonRadius='8px'
                buttonSize='40px'
                text='Delete'
                isDanger
                variant='outlined'
                onClick={() => deleteNotification(openIndex)}
              />
              <Button2
                buttonRadius='8px'
                buttonSize='40px'
                text='Edit'
                variant='outlined'
                leftElement={<Icon iconName='Pencil1' size={14} />}
                onClick={() => startEdit(openIndex)}
              />
              <Button2 buttonRadius='8px' buttonSize='40px' text='Close' variant='contained' onClick={() => setOpenIndex(null)} />
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default NotificationsList
